import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { LINKS, NIMNY_AVATAR, getNimnyAppUrl } from '../../data/translations';

// Custom social icons
const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
  </svg>
);

const FooterNew = () => {
  const { language } = useLanguage();

  // Get localized URLs
  const getLocalizedUrl = (basePath) => {
    if (language === 'ru') return `/ru${basePath}`;
    if (language === 'es') return `/es${basePath}`;
    return basePath;
  };

  // Footer translations
  const footerText = {
    privacy: {
      en: 'Privacy',
      es: 'Privacidad',
      ru: 'Конфиденциальность'
    }[language],
    terms: {
      en: 'Terms',
      es: 'Términos',
      ru: 'Условия'
    }[language],
    contact: {
      en: 'Contact',
      es: 'Contacto',
      ru: 'Контакты'
    }[language],
    about: {
      en: 'About',
      es: 'Acerca',
      ru: 'О нас'
    }[language],
    read: {
      en: 'Read Stories',
      es: 'Leer historias',
      ru: 'Читать сказки'
    }[language]
  };

  return (
    <footer className="relative py-12 md:py-16 border-t border-amber-200/10">
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="flex flex-col items-center gap-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div 
              className="w-8 h-8 rounded-full overflow-hidden border-2 border-amber-300/40"
              style={{ backgroundColor: '#d4a574' }}
            >
              <img 
                src={NIMNY_AVATAR}
                alt="Nimny"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.15)', objectPosition: 'center 40%' }}
              />
            </div>
            <span className="font-sans text-lg text-[#f5ebe0]/90">Nimny</span>
          </Link>

          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a
              href={getNimnyAppUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/50 hover:text-amber-200/80 transition-colors font-sans"
            >
              {footerText.read}
            </a>
            <a
              href={LINKS.amazon}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/50 hover:text-amber-200/80 transition-colors font-sans"
            >
              Amazon
            </a>
            <Link
              to={getLocalizedUrl('/about-nimny')}
              className="text-amber-200/50 hover:text-amber-200/80 transition-colors font-sans"
            >
              {footerText.about}
            </Link>
            <Link
              to={getLocalizedUrl('/contact')}
              className="text-amber-200/50 hover:text-amber-200/80 transition-colors font-sans"
              data-testid="footer-contact"
            >
              {footerText.contact}
            </Link>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-5">
            <a
              href="https://www.instagram.com/nimny_stories"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/30 hover:text-amber-200/60 transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://www.tiktok.com/@nimny_stories"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/30 hover:text-amber-200/60 transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon />
            </a>
            <a
              href="https://www.youtube.com/@nimny_stories"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/30 hover:text-amber-200/60 transition-colors"
              aria-label="YouTube"
            >
              <YouTubeIcon />
            </a>
            <a
              href="https://www.facebook.com/share/1977hUhHun/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/30 hover:text-amber-200/60 transition-colors"
              aria-label="Facebook"
              data-testid="footer-facebook-link"
            >
              <FacebookIcon />
            </a>
          </div>

          {/* Divider */}
          <div className="w-16 h-px bg-amber-200/10" />

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs">
            <Link
              to={getLocalizedUrl('/privacy')}
              className="text-amber-200/30 hover:text-amber-200/50 transition-colors font-sans"
              data-testid="footer-privacy"
            >
              {footerText.privacy}
            </Link>
            <span className="text-amber-200/20">·</span>
            <Link
              to={getLocalizedUrl('/terms')}
              className="text-amber-200/30 hover:text-amber-200/50 transition-colors font-sans"
              data-testid="footer-terms"
            >
              {footerText.terms}
            </Link>
          </div>

          {/* Copyright */}
          <p className="text-amber-200/20 font-sans text-xs" data-testid="footer-copyright">
            © 2026 Nimny LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterNew;
